<main class="mx-auto w-full max-w-5xl px-0 pb-12 text-strong sm:px-2">
    <header class="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div class="min-w-0">
            <h1 class="m-0 text-2xl font-bold tracking-tight text-strong sm:text-3xl">La tua area riservata</h1>
            <p class="mb-0 mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                Qui gestisci gli atleti iscritti tramite il tuo profilo. Una volta approvati dalla società sportiva potrai
                modificarne i dati, pagare le rette e caricare i documenti richiesti.
            </p>
        </div>
        <a class="btn btn-primary inline-flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 sm:w-auto"
           [routerLink]="['/users/athletes/new']">
            <i class="bi bi-person-plus" aria-hidden="true"></i>
            Iscrivi nuovo atleta
        </a>
    </header>

    <nav class="mb-8 grid grid-cols-2 gap-3 sm:gap-4" aria-label="Comandi utili">
        <a [routerLink]="['/users/messages']"
           class="group flex min-h-[72px] items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 !text-strong transition hover:border-primary hover:bg-primary-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:p-4">
            <span class="relative grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary-soft text-lg text-primary">
                <i class="bi bi-chat-left-text" aria-hidden="true"></i>
                <span *ngIf="numberOfNewMessages >= 1"
                      class="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-danger px-1 text-[11px] font-bold leading-none text-white sm:hidden">
                    {{ numberOfNewMessages }}
                </span>
            </span>
            <span class="flex min-w-0 flex-col">
                <span class="text-sm font-semibold sm:text-base">Comunicazioni</span>
                <span class="hidden truncate text-xs text-slate-600 sm:block">
                    <ng-container *ngIf="numberOfNewMessages === 0">Nessun nuovo messaggio</ng-container>
                    <ng-container *ngIf="numberOfNewMessages === 1">1 nuovo messaggio</ng-container>
                    <ng-container *ngIf="numberOfNewMessages > 1">{{ numberOfNewMessages }} nuovi messaggi</ng-container>
                </span>
            </span>
            <i class="bi bi-chevron-right ml-auto hidden text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-primary sm:block" aria-hidden="true"></i>
        </a>
        <a [routerLink]="['/users/convocations']"
           class="group flex min-h-[72px] items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 !text-strong transition hover:border-primary hover:bg-primary-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:p-4">
            <span class="relative grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary-soft text-lg text-primary">
                <i class="bi bi-calendar-event" aria-hidden="true"></i>
                <span *ngIf="numberOfNewConvocations >= 1"
                      class="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-primary px-1 text-[11px] font-bold leading-none text-white sm:hidden">
                    {{ numberOfNewConvocations }}
                </span>
            </span>
            <span class="flex min-w-0 flex-col">
                <span class="text-sm font-semibold sm:text-base">Convocazioni</span>
                <span class="hidden truncate text-xs text-slate-600 sm:block">
                    <ng-container *ngIf="numberOfNewConvocations === 0">Nessuna convocazione futura</ng-container>
                    <ng-container *ngIf="numberOfNewConvocations === 1">1 convocazione futura</ng-container>
                    <ng-container *ngIf="numberOfNewConvocations > 1">{{ numberOfNewConvocations }} convocazioni future</ng-container>
                </span>
            </span>
            <i class="bi bi-chevron-right ml-auto hidden text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-primary sm:block" aria-hidden="true"></i>
        </a>
    </nav>

    <section aria-labelledby="athletes-heading">
        <div class="mb-3 flex items-baseline justify-between gap-3">
            <h2 id="athletes-heading" class="m-0 text-lg font-bold text-strong">I tuoi atleti</h2>
            <span class="text-sm text-slate-600" *ngIf="!loadingAthletes && athletes.length > 0">
                {{ athletes.length }} {{ athletes.length === 1 ? 'atleta' : 'atleti' }}
            </span>
        </div>

        <div *ngIf="loadingAthletes" class="flex flex-col gap-3 sm:gap-4" role="status" aria-label="Caricamento atleti">
            <div *ngFor="let skeleton of [1, 2]" class="flex animate-pulse items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:gap-4 sm:p-5">
                <div class="h-14 w-14 shrink-0 rounded-lg bg-slate-200 sm:h-16 sm:w-16"></div>
                <div class="flex min-w-0 flex-1 flex-col gap-2">
                    <div class="h-4 w-40 max-w-full rounded bg-slate-200"></div>
                    <div class="h-5 w-28 rounded-md bg-slate-100"></div>
                    <div class="h-3 w-48 max-w-full rounded bg-slate-100"></div>
                </div>
                <div class="hidden h-11 w-36 shrink-0 rounded-xl bg-slate-100 md:block"></div>
            </div>
        </div>

        <div *ngIf="!loadingAthletes && athletes.length > 0" class="flex flex-col gap-3 sm:gap-4">
            <ui-athlete-card *ngFor="let athlete of athletes" [athlete]="athlete"></ui-athlete-card>
        </div>

        <ng-container *ngIf="!loadingAthletes && athletes.length === 0">
            <div class="flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
                <span class="mb-3 grid h-12 w-12 place-items-center rounded-full bg-primary-soft text-xl text-primary">
                    <i class="bi bi-people" aria-hidden="true"></i>
                </span>
                <p class="m-0 font-semibold text-strong">Nessun atleta associato al tuo profilo</p>
                <p class="mb-5 mt-1 max-w-sm text-sm text-slate-600">Iscrivi il primo atleta per inviare la richiesta alla società sportiva.</p>
                <a class="btn btn-primary inline-flex min-h-[44px] w-full items-center justify-center gap-2 sm:w-auto"
                   [routerLink]="['/users/athletes/new']">
                    <i class="bi bi-person-plus" aria-hidden="true"></i>
                    Iscrivi nuovo atleta
                </a>
            </div>
        </ng-container>
    </section>
</main>
